import { Box, BoxProps } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby-link";
import React, { useState } from "react";
import useAuth from "../../../../hooks/use-auth";
import { analyticsTrackEvent } from "../../../../services/analytics";
import { reportSentryClientError } from "../../../../services/sentry";
import {
  sendUserAuthenticationEmailOtp,
  sendUserAuthenticationPhoneNumberOtp,
  updateUser,
  verifyUserAuthenticationEmailOtp,
  verifyUserAuthenticationPhoneNumberOtp
} from "../../../../services/user";
import { UserType } from "../../../../types/airgraft";
import AlreadySignedIn from "../../components/AlreadySignedIn";
import EmailOtpVerificationForm from "./Form/EmailOtpVerificationForm";
import PhoneNumberOtpVerificationForm from "./Form/PhoneNumberOtpVerificationForm";
import RegisterForm, { RegisterFormDataType } from "./Form/RegisterForm";
import SignInEmailForm, {
  SignInEmailFormDataType
} from "./Form/SignInEmailForm";
import SignInPhoneNumberForm, {
  SignInPhoneNumberFormDataType
} from "./Form/SignInPhoneNumberForm";

type Props = {
  type: "sign-in" | "register";
  onAuthentication: (user: UserType, isNewUser: boolean) => void;
  fullHeight?: boolean;
  labels?: {
    signIn?: { title?: string };
    register?: { title?: string; subtitle?: string };
  };
  showLoading?: boolean;
} & BoxProps;

type StateType = {
  step: "form" | "confirmation";
  signInMode?: "phone-number" | "email";
  registerData?: RegisterFormDataType;
  signInData?: { phoneNumber?: string; email?: string };
  showLoading?: boolean;
};

export default function Authenticate({
  type,
  onAuthentication,
  fullHeight,
  labels,
  showLoading,
  ...boxProps
}: Props) {
  const { pathname } = useLocation();
  const { isAuthenticated, user, logout, updateLocalStateUser } = useAuth();

  const [state, setState] = useState<StateType>({
    step: "form",
    signInMode: "phone-number",
    showLoading: false
  });

  const fullHeightStyles = {
    minHeight: {
      xs: "calc(var(--100vh, 100vh) - var(--header-height))",
      sm: "0px"
    },
    display: "flex",
    flexDirection: "column",
    justifyContent: { xs: "space-between", sm: "flex-start" }
  };

  const wrapperProps = {
    ...(fullHeight ? fullHeightStyles : {}),
    ...(boxProps || {})
  };

  const handleRegisterFormSubmit = (registerData: RegisterFormDataType) => {
    setState({ step: "confirmation", registerData });
  };

  const handleRegisterOtpVerificationSubmit = async (otp: string) => {
    let user = await verifyUserAuthenticationEmailOtp(
      state.registerData.email,
      otp
    );

    setState({ ...state, showLoading: true });

    updateLocalStateUser(user);

    // Update user.givenName & user.familyName
    try {
      user = await updateUser({
        givenName: state.registerData.givenName,
        familyName: state.registerData.familyName,
        email: state.registerData.email
        // phoneNumber: state.registerData.phoneNumber
      });
      if (user) {
        updateLocalStateUser(user);
      }
    } catch (e) {
      reportSentryClientError(e);
    }

    analyticsTrackEvent("sign-up", {
      userUuid: user.uuid
    });

    onAuthentication(user, true);
  };

  const handleSignInPhoneNumberFormSubmit = async (
    signInData: SignInEmailFormDataType
  ) => {
    setState({ step: "confirmation", signInMode: "email", signInData });
  };

  const handleSignInEmailFormSubmit = async (
    signInData: SignInEmailFormDataType
  ) => {
    setState({ step: "confirmation", signInMode: "email", signInData });
  };

  const handleSignInOtpVerificationSubmit = async (otp: string) => {
    let user = null;
    if (state.signInMode === "phone-number") {
      user = await verifyUserAuthenticationPhoneNumberOtp(
        state.signInData.phoneNumber,
        otp
      );
    } else {
      user = await verifyUserAuthenticationEmailOtp(
        state.signInData.email,
        otp
      );
    }

    setState({ ...state, showLoading: true });

    updateLocalStateUser(user);

    analyticsTrackEvent("sign-in", {
      method: "phone-number",
      userUuid: user.uuid
    });

    onAuthentication(user, false);
  };

  const switchtoSignIn = () => {
    navigate(pathname.replace("/register", "/sign-in"));
  };

  const switchtoRegister = () => {
    navigate(pathname.replace("/sign-in", "/register"));
  };

  const switchToSignInEmail = () => {
    setState({ step: "form", signInMode: "email" });
  };

  const switchToSignInPhoneNumber = () => {
    setState({ step: "form", signInMode: "phone-number" });
  };

  const handleChangeAccount = () => {
    logout();
    switchtoSignIn();
  };

  // Loading
  if (state.showLoading || showLoading) {
    return (
      <Box {...wrapperProps}>
        <Box>
          <Skeleton width="50%" height="32px" animation="wave" />
          <Skeleton width="100%" height="32px" animation="wave" />
          <Skeleton width="80%" height="32px" animation="wave" />
        </Box>
      </Box>
    );
  }

  // Already authenticated
  if (isAuthenticated) {
    return (
      <Box {...wrapperProps}>
        <AlreadySignedIn
          onContinue={() => onAuthentication(user, false)}
          onChangeAccount={handleChangeAccount}
        />
      </Box>
    );
  }

  // Authentication form
  return (
    <Box {...wrapperProps}>
      {/* Register (Create account) */}
      {type == "register" && state.step === "form" && (
        <RegisterForm
          onSubmit={handleRegisterFormSubmit}
          labels={labels?.register}
          onSwitchToSignIn={switchtoSignIn}
        />
      )}
      {type == "register" && state.step === "confirmation" && (
        <EmailOtpVerificationForm
          email={state.registerData.email}
          onVerificationOtpSubmit={handleRegisterOtpVerificationSubmit}
          onResendOtpCode={sendUserAuthenticationEmailOtp}
        />
      )}

      {/* Sign In (Phone number) */}
      {type == "sign-in" &&
        state.step === "form" &&
        state.signInMode === "phone-number" && (
          <SignInEmailForm
            onSubmit={handleSignInPhoneNumberFormSubmit}
            labels={labels?.signIn}
            onSwitchToRegister={switchtoRegister}
            onSwitchToPhoneNumberSignIn={switchToSignInPhoneNumber}
          />
        )}
      {type == "sign-in" &&
        state.step === "confirmation" &&
        state.signInMode === "phone-number" && (
          <PhoneNumberOtpVerificationForm
            phoneNumber={state.signInData.phoneNumber}
            onVerificationOtpSubmit={handleSignInOtpVerificationSubmit}
            onResendOtpCode={sendUserAuthenticationPhoneNumberOtp}
          />
        )}

      {/* Sign in (Email) */}
      {type == "sign-in" &&
        state.step === "form" &&
        state.signInMode === "email" && (
          <SignInEmailForm
            onSubmit={handleSignInEmailFormSubmit}
            labels={labels?.signIn}
            onSwitchToRegister={switchtoRegister}
            onSwitchToPhoneNumberSignIn={switchToSignInPhoneNumber}
          />
        )}
      {type == "sign-in" &&
        state.step === "confirmation" &&
        state.signInMode === "email" && (
          <EmailOtpVerificationForm
            email={state.signInData.email}
            onVerificationOtpSubmit={handleSignInOtpVerificationSubmit}
            onResendOtpCode={sendUserAuthenticationEmailOtp}
          />
        )}
    </Box>
  );
}
